import React, { ChangeEvent, useEffect, useRef } from "react";

import { noop } from "lodash-es";
import mergeRefs from "merge-refs";
import { twJoin } from "tailwind-merge";

type InputProps = React.HTMLProps<HTMLInputElement> & {
  id?: string;
  checked?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  dataLoc?: string;

  /**
   * Indeterminate state takes visual precedence over the checked state
   * This means that if both `checked` and `indeterminate` are true,
   * the checkbox will be in indeterminate state.
   */
  indeterminate?: boolean;
  ref?: React.Ref<HTMLInputElement>;
};

export const Checkbox: React.FC<InputProps> = ({
  id,
  checked,
  onChange,
  className,
  dataLoc,
  indeterminate = false,
  ref,
  ...props
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!checkboxRef.current) return;
    checkboxRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={mergeRefs(ref, checkboxRef)}
      checked={checked}
      className={twJoin(
        "h-3 w-3 rounded-sm border border-gray-400 text-gray-0 accent-indigo-600",
        props.disabled ? "cursor-not-allowed" : "cursor-pointer",
        className,
      )}
      data-loc={dataLoc}
      id={id}
      type="checkbox"
      onChange={props.readOnly ? noop : onChange}
      {...props}
    />
  );
};
